/* Pathway Coffee — shared purchase engine
   File: assets/pathway-appstle.js

   SINGLE SOURCE OF TRUTH for pricing, selling-plan resolution and the Fresh
   Roast schedule. The product page (sections/pathway-product-main.liquid) and
   the collection cards (snippets/pathway-product-card.liquid) both delegate
   here, so the two pages cannot drift apart.

   How the subscription price is determined
   ----------------------------------------
   Subscription price is NOT variant.selling_plan_allocations[].price. It is
   Appstle's volume-discount tier total, read from
   window._ABConfig.bundle_rules and normalised by normalizeAppstleTiers():

       tier.total = (variant.price / 100 * tier.quantity) - tier.discountAmount

   That is exactly how the product page has always computed the number the
   customer sees, so reusing it is what guarantees price parity. Quantity does
   not multiply a unit price — each bag count is its own tier with its own
   total. When no tier exists for a quantity there is NO subscription price;
   callers must show a loading/unavailable state rather than inventing one.

   Exception (matches getSubscriptionPricing() on the product page): when the
   resolved selling plan has a real allocation price, the engine shows that
   price — it is what Shopify charges — and the tier only gates which bag
   counts are offered.

   One-time price is plain Shopify: variant.price / 100 * quantity.

   Nothing in this file hard-codes a discount percentage, dollar amount,
   selling-plan id or tier price.
*/
(function (global) {
  'use strict';

  var ONE_DAY = 1000 * 60 * 60 * 24;

  /* ==================================================================
     Formatting / parsing
     ================================================================== */

  function money(value) {
    return '$' + Number(value || 0).toFixed(2);
  }

  function moneyFromCents(cents) {
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function parseJsonValue(value, fallback) {
    if (value == null) return fallback;
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function bagLabel(count) {
    return count === 1 ? '1 bag' : count + ' bags';
  }

  /**
   * Parses a YYYY-MM-DD theme setting into a local Date anchored at noon.
   * Noon avoids the UTC-midnight parse shifting the date back a day in
   * western timezones. Matches parseIsoDateString() on the product page.
   */
  function parseIsoDateString(value) {
    if (value instanceof Date) return value;
    if (typeof value !== 'string') return null;

    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!match) return null;

    var year = Number(match[1]);
    var month = Number(match[2]);
    var day = Number(match[3]);
    var date = new Date(year, month - 1, day, 12, 0, 0, 0);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  /* ==================================================================
     Variants
     ================================================================== */

  function findVariantForOptions(variants, optionSelections) {
    if (!Array.isArray(variants)) return null;

    return variants.find(function (variant) {
      if (!Array.isArray(variant.options)) return false;

      return variant.options.every(function (value, index) {
        var selectedValue = optionSelections[index + 1];
        return selectedValue == null || String(selectedValue) === String(value);
      });
    }) || null;
  }

  function isOptionValueAvailable(variants, optionSelections, position, value) {
    var candidate = Object.assign({}, optionSelections);
    candidate[position] = value;

    var variant = findVariantForOptions(variants, candidate);
    return Boolean(variant && variant.available);
  }

  /* ==================================================================
     Inventory-aware initial fulfillment
     ==================================================================
     Pure, DOM-free. Answers "does the store currently have enough tracked
     inventory for this variant to cover what's already in the customer's
     cart plus what they're about to add?" — nothing here talks to Shopify,
     Appstle, or the DOM, and nothing here ever changes variant.available
     (Shopify's own "can this be added to the cart at all" signal stays
     exactly as Shopify computed it; this is a separate, additional signal
     about *when* the initial order can realistically ship).
     ================================================================== */

  /**
   * Clamps any input to a safe, non-negative integer. Bad input (NaN,
   * negative, a string, undefined) becomes 0 rather than throwing or
   * producing NaN downstream.
   */
  function normalizeNonNegativeInt(value) {
    var num = Number(value);
    if (!isFinite(num) || num < 0) return 0;
    return Math.floor(num);
  }

  /**
   * @param {Object} variant                Must (optionally) carry
   *   inventoryQuantity (number|null|undefined) and inventoryManagement
   *   (string|null|undefined) — the fields added to the product-data JSON
   *   alongside the existing id/title/options/available/price/sellingPlans.
   *   Never mutated.
   * @param {number} requestedQuantity      Quantity about to be added.
   * @param {number} [existingCartQuantity] Quantity of this same variant
   *   already sitting in the customer's cart (any selling plan), 0 if
   *   unknown/not supplied.
   * @returns {{
   *   tracked: boolean,
   *   inventoryQuantity: number|null,
   *   requestedQuantity: number,
   *   existingCartQuantity: number,
   *   totalRequestedQuantity: number,
   *   status: 'sufficient'|'insufficient'|'unknown',
   *   reviewRequired: boolean
   * }}
   *
   * Rules (see the audit + implementation prompt for the business context):
   *  - Untracked variant (inventoryManagement is null/blank) -> always
   *    'sufficient', reviewRequired:false. Shopify itself imposes no cap on
   *    an untracked variant, so there is nothing to flag.
   *  - Tracked variant with a valid numeric inventoryQuantity -> 'sufficient'
   *    when inventoryQuantity >= (requested + existing cart quantity),
   *    otherwise 'insufficient' (this also covers zero/negative on-hand).
   *  - Tracked variant with missing/malformed inventoryQuantity -> 'unknown',
   *    reviewRequired:true. We never guess "sufficient" when we can't tell.
   */
  function getInventoryStatus(variant, requestedQuantity, existingCartQuantity) {
    var requested = normalizeNonNegativeInt(requestedQuantity);
    var existing = normalizeNonNegativeInt(existingCartQuantity);
    var total = requested + existing;

    var managementRaw = variant ? variant.inventoryManagement : null;
    var tracked = Boolean(managementRaw) && String(managementRaw).trim() !== '';

    var rawQuantity = variant ? variant.inventoryQuantity : undefined;
    var quantityIsValid = typeof rawQuantity === 'number' && isFinite(rawQuantity);

    var result = {
      tracked: tracked,
      inventoryQuantity: quantityIsValid ? rawQuantity : null,
      requestedQuantity: requested,
      existingCartQuantity: existing,
      totalRequestedQuantity: total,
      status: 'sufficient',
      reviewRequired: false
    };

    if (!tracked) {
      // Untracked: Shopify itself never limits this variant by quantity.
      result.status = 'sufficient';
      result.reviewRequired = false;
      return result;
    }

    if (!quantityIsValid) {
      // Tracked, but we don't have a trustworthy number (missing field,
      // failed cart fetch upstream, malformed data). Never claim
      // "sufficient" here — flag it for a human instead.
      result.status = 'unknown';
      result.reviewRequired = true;
      return result;
    }

    if (rawQuantity >= total) {
      result.status = 'sufficient';
      result.reviewRequired = false;
    } else {
      result.status = 'insufficient';
      result.reviewRequired = true;
    }

    return result;
  }

  /**
   * Formats a Date as an unambiguous YYYY-MM-DD string using local date
   * components (never toISOString(), which is UTC and can shift the date
   * by a day). Used for internal line-item properties only — customer-
   * facing display text continues to use formatDate()/toLocaleDateString.
   */
  function formatIsoDate(date) {
    if (!date || isNaN(date.getTime && date.getTime())) return '';
    var year = date.getFullYear();
    var month = String(date.getMonth() + 1).padStart(2, '0');
    var day = String(date.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  /* ==================================================================
     Dates and billing cycles
     ================================================================== */

  function daysBetween(start, end) {
    var startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    var endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.round((endUtc - startUtc) / ONE_DAY);
  }

  function nextBillingDate(billingDay, minimumDays) {
    var today = new Date();
    var candidate = new Date(today.getFullYear(), today.getMonth(), Number(billingDay));

    if (candidate < today) {
      candidate = new Date(today.getFullYear(), today.getMonth() + 1, Number(billingDay));
    }

    while (daysBetween(today, candidate) < Number(minimumDays)) {
      candidate = new Date(candidate.getFullYear(), candidate.getMonth() + 1, Number(billingDay));
    }

    return candidate;
  }

  function findPlanForCycle(variant, cycle) {
    if (!variant || !Array.isArray(variant.sellingPlans) || !cycle) return null;

    var configuredId = String(cycle.planId || '').trim();
    if (configuredId) {
      var byId = variant.sellingPlans.find(function (plan) {
        return String(plan.id) === configuredId;
      });
      if (byId) return byId;
    }

    var normalizedMatch = String(cycle.planMatch || '').trim().toLowerCase();
    if (normalizedMatch) {
      var byName = variant.sellingPlans.find(function (plan) {
        return String(plan.name || '').toLowerCase().indexOf(normalizedMatch) !== -1;
      });
      if (byName) return byName;
    }

    return null;
  }

  function getCycleCandidates(variant, cycleConfig) {
    var config = cycleConfig || {};
    var minimumDays = Number(config.minimumDays || 21);
    var targetDays = Number(config.targetDays || 28);

    return [config.firstWeek, config.thirdWeek]
      .filter(Boolean)
      .map(function (cycle) {
        var plan = findPlanForCycle(variant, cycle);
        var renewalDate = nextBillingDate(cycle.billingDay, minimumDays);
        var renewalDays = daysBetween(new Date(), renewalDate);

        return {
          key: cycle.key,
          window: cycle.window,
          billingDay: Number(cycle.billingDay),
          plan: plan,
          renewalDate: renewalDate,
          renewalDays: renewalDays,
          score: Math.abs(renewalDays - targetDays)
        };
      })
      .filter(function (cycle) {
        return Boolean(cycle.plan);
      })
      .sort(function (a, b) {
        if (a.score !== b.score) return a.score - b.score;
        return a.renewalDays - b.renewalDays;
      });
  }

  /**
   * Prelaunch forces one specific plan, matched by exact numeric id against
   * the global theme setting, with merchant-configured ship/billing dates.
   */
  function getPrelaunchPlanCycle(variant, prelaunch) {
    if (!variant || !Array.isArray(variant.sellingPlans)) return null;
    if (!prelaunch || !prelaunch.enabled) return null;

    var planId = String(prelaunch.planId || '').trim();
    if (!planId || !/^\d+$/.test(planId)) return null;

    var matchedPlan = variant.sellingPlans.find(function (plan) {
      return String(plan.id) === planId;
    });

    if (!matchedPlan) return null;

    var billingDate = parseIsoDateString(prelaunch.nextBillingDate);
    var shipDate = parseIsoDateString(prelaunch.shipDate);

    return {
      key: 'prelaunch-first-week',
      window: prelaunch.window || 'First week of each month (days 1–7)',
      billingDay: 25,
      plan: matchedPlan,
      renewalDate: billingDate,
      renewalDays: billingDate ? daysBetween(new Date(), billingDate) : null,
      shipDateOverride: shipDate,
      score: 0,
      isPrelaunch: true
    };
  }

  function isPrelaunchConfigValid(prelaunch) {
    if (!prelaunch || !prelaunch.enabled) return true;

    return Boolean(
      prelaunch.planId &&
      /^\d+$/.test(String(prelaunch.planId).trim()) &&
      parseIsoDateString(prelaunch.shipDate) &&
      parseIsoDateString(prelaunch.nextBillingDate)
    );
  }

  function getSelectedCycle(variant, cycleConfig, prelaunch) {
    if (prelaunch && prelaunch.enabled) {
      if (!isPrelaunchConfigValid(prelaunch)) return null;
      return getPrelaunchPlanCycle(variant, prelaunch);
    }

    return getCycleCandidates(variant, cycleConfig)[0] || null;
  }

  function getSellingPlan(variant, cycleConfig, prelaunch) {
    var cycle = getSelectedCycle(variant, cycleConfig, prelaunch);
    return cycle ? cycle.plan : null;
  }

  function getCycleShipDate(cycle) {
    if (!cycle) return null;
    if (cycle.shipDateOverride) return cycle.shipDateOverride;
    if (!cycle.renewalDate) return null;

    var shipDate = new Date(cycle.renewalDate);

    if (cycle.key === 'first-week') {
      shipDate = new Date(shipDate.getFullYear(), shipDate.getMonth() + 1, 1);
    } else if (cycle.key === 'third-week') {
      shipDate = new Date(shipDate.getFullYear(), shipDate.getMonth(), 15);
    }

    return shipDate;
  }

  function formatDate(date, options) {
    if (!date) return '';
    return new Date(date).toLocaleDateString(undefined, options || { month: 'long', day: 'numeric' });
  }

  function formatBillingDate(cycle, options) {
    if (!cycle || !cycle.renewalDate) return '';
    return formatDate(cycle.renewalDate, options);
  }

  function formatCustomerShipDate(cycle, options) {
    return formatDate(getCycleShipDate(cycle), options);
  }

  /**
   * The long-form ship label the product page renders, e.g.
   * "First week of November", or the exact date during prelaunch.
   */
  function formatShipLabel(cycle) {
    if (!cycle) return '';
    if (cycle.isPrelaunch) return formatCustomerShipDate(cycle);

    var shipDate = getCycleShipDate(cycle);
    var shipMonth = shipDate ? shipDate.toLocaleDateString(undefined, { month: 'long' }) : '';

    return cycle.key === 'first-week'
      ? 'First week of ' + shipMonth
      : 'Third week of ' + shipMonth;
  }

  function formatCycleWindow(cycle) {
    if (!cycle) return '';

    return cycle.window +
      '. Your first eligible recurring charge is approximately ' +
      cycle.renewalDays +
      ' days after today.';
  }

  function shortCycleLabel(cycle) {
    if (!cycle || !cycle.window) return '';
    return String(cycle.window).replace(/\s*\([^)]*\)\s*$/, '').trim();
  }

  /* ==================================================================
     Appstle bundle rules
     ================================================================== */

  function getAppstleRule(productId, variant) {
    var config = global._ABConfig || {};
    var rules = Array.isArray(config.bundle_rules) ? config.bundle_rules : [];

    return rules.find(function (rule) {
      if (!rule || rule.status !== 'ACTIVE' || rule.bundleType !== 'VOLUME_DISCOUNT') {
        return false;
      }

      if (rule.appliesOn && rule.appliesOn !== 'SUBSCRIPTION' && rule.appliesOn !== 'BOTH') {
        return false;
      }

      var variants = parseJsonValue(rule.variants, []);
      var products = parseJsonValue(rule.products, []);

      if (variant && variants.length) {
        var variantMatch = variants.some(function (item) {
          return String(item.variantId || item.id) === String(variant.id);
        });
        if (variantMatch) return true;
      }

      if (products.length) {
        var productMatch = products.some(function (item) {
          return String(item.productId || item.id) === String(productId);
        });
        if (productMatch) return true;
      }

      // Rules injected only onto applicable products omit both arrays.
      return !variants.length && !products.length;
    }) || null;
  }

  function normalizeAppstleTiers(rule, variant) {
    if (!rule) return [];

    var rawTiers = parseJsonValue(rule.tieredDiscount, []);
    var baseUnit = variant ? Number(variant.price) / 100 : 0;

    return rawTiers
      .map(function (tier) {
        var quantity = Number(tier.value || tier.quantity || tier.minimumQuantity || 0);
        var regularTotal = baseUnit * quantity;
        var discountType = String(tier.discountType || '').toUpperCase();
        var discountValue = Number(tier.discount || tier.discountValue || 0);
        var discountAmount = discountType === 'PERCENTAGE'
          ? regularTotal * (discountValue / 100)
          : discountValue;
        var total = Math.max(0, regularTotal - discountAmount);

        return {
          quantity: quantity,
          title: tier.titleLabel || quantity + (quantity === 1 ? ' Bag' : ' Bags'),
          subtitle: tier.subtitleLabel || '',
          badge: tier.badgeLabel || '',
          badgeType: tier.badgeType || '',
          discountType: discountType,
          discountValue: discountValue,
          discountAmount: discountAmount,
          regularTotal: regularTotal,
          total: total,
          unit: quantity > 0 ? total / quantity : 0,
          freeShipping: /free shipping/i.test(tier.subtitleLabel || ''),
          purchaseType: tier.tierPurchaseType || ''
        };
      })
      .filter(function (tier) {
        return tier.quantity > 0 && (!tier.purchaseType || tier.purchaseType === 'SUBSCRIPTION');
      })
      .sort(function (a, b) {
        return a.quantity - b.quantity;
      });
  }

  function getTier(tiers, quantity) {
    if (!Array.isArray(tiers)) return null;

    return tiers.find(function (tier) {
      return tier.quantity === Number(quantity);
    }) || null;
  }

  /**
   * Min/max bag counts the merchant actually configured. The product page
   * clamps its stepper to these, so the collection must too.
   */
  function tierBounds(tiers) {
    if (!Array.isArray(tiers) || !tiers.length) return { min: 1, max: 4 };

    var quantities = tiers.map(function (tier) {
      return tier.quantity;
    });

    return {
      min: Math.min.apply(null, quantities),
      max: Math.max.apply(null, quantities)
    };
  }

  /**
   * The next tier up from the current quantity, but only when it genuinely
   * lowers the per-bag price. Used for honest "add one more bag" messaging —
   * returns null rather than guessing when no such tier exists.
   */
  function nextBetterTier(tiers, quantity) {
    var current = getTier(tiers, quantity);
    if (!Array.isArray(tiers) || !current) return null;

    return tiers.find(function (tier) {
      return tier.quantity > Number(quantity) && tier.unit < current.unit - 0.005;
    }) || null;
  }

  /* ==================================================================
     Pricing engine
     ==================================================================
     One call answers: "what would this store charge for this product, in
     this configuration, right now?" Both the PDP and the collection cards
     render whatever this returns.
     ================================================================== */

  function createPricingEngine(productData, config) {
    var data = productData || {};
    var settings = config || {};
    var variants = Array.isArray(data.variants) ? data.variants : [];

    var cachedRuleVariantId = null;
    var cachedTiers = [];

    function tiersFor(variant) {
      if (!variant) return [];

      // Rules can be variant-specific, so re-read when the variant changes.
      if (cachedRuleVariantId !== variant.id) {
        cachedRuleVariantId = variant.id;
        cachedTiers = normalizeAppstleTiers(getAppstleRule(data.productId, variant), variant);
      }

      return cachedTiers;
    }

    function invalidate() {
      cachedRuleVariantId = null;
      cachedTiers = [];
    }

    /**
     * @param {Object} state
     * @param {Object} state.selectedOptions  1-based option position -> value
     * @param {number} state.quantity
     * @param {string} state.mode             'subscription' | 'onetime'
     * @returns {Object} resolved purchase state
     */
    function resolve(state) {
      var input = state || {};
      var mode = input.mode === 'onetime' ? 'onetime' : 'subscription';
      var quantity = Math.max(1, Number(input.quantity) || 1);

      var variant =
        findVariantForOptions(variants, input.selectedOptions || {}) ||
        variants.find(function (item) {
          return String(item.id) === String(data.selectedVariantId);
        }) ||
        variants[0] ||
        null;

      var result = {
        mode: mode,
        variant: variant,
        variantId: variant ? variant.id : null,
        quantity: quantity,
        available: Boolean(variant && variant.available),
        oneTimeUnit: variant ? Number(variant.price) / 100 : 0,
        subscriptionEligible: false,
        cycle: null,
        plan: null,
        sellingPlanId: null,
        tier: null,
        tiers: [],
        bounds: { min: 1, max: 12 },
        unitPrice: 0,
        total: 0,
        savings: 0,
        compareTotal: 0,
        freeShipping: false,
        nextTier: null,
        priceSource: 'onetime',
        fellBackToOneTime: false,
        ready: false,
        reason: ''
      };

      if (!variant) {
        result.reason = 'no-variant';
        return result;
      }

      result.compareTotal = result.oneTimeUnit * quantity;

      /* ---- One-time: plain Shopify pricing, always resolvable ---- */
      if (mode === 'onetime') {
        result.unitPrice = result.oneTimeUnit;
        result.total = result.oneTimeUnit * quantity;
        result.bounds = { min: 1, max: 12 };
        result.ready = true;
        return result;
      }

      /*
       * ---- Subscription ----
       *
       * Three distinct states, deliberately kept apart:
       *
       *   1. NOT SUBSCRIBABLE  - no selling plans, or none matching the
       *      configured cycles. The product is an ordinary Shopify product.
       *      subscriptionEligible stays false and the caller must fall back
       *      to one-time entirely: no toggle, no savings, no schedule.
       *
       *   2. SUBSCRIBABLE, NO BUNDLE TIER - a plan resolves but Appstle has
       *      no volume rule covering this quantity. Price comes from the
       *      selling plan's own allocation price, which is what Shopify
       *      charges. Real Appstle data, not a calculation.
       *
       *   3. FULLY ELIGIBLE - a plan resolves and a tier matches the
       *      quantity. Price is tier.total, identical to the product page.
       *
       * A missing selling plan is NOT the same as a missing bundle tier, and
       * neither one may ever leave the caller without a usable price.
       */
      var cycle = getSelectedCycle(variant, settings.cycleConfig, settings.prelaunch);
      var plan = cycle ? cycle.plan : null;
      var prelaunchBroken = Boolean(
        settings.prelaunch &&
        settings.prelaunch.enabled &&
        !isPrelaunchConfigValid(settings.prelaunch)
      );

      // State 1: treat a broken prelaunch config as "not subscribable" too,
      // so customers can still buy the product one-time.
      if (prelaunchBroken || !plan) {
        result.subscriptionEligible = false;
        result.reason = prelaunchBroken ? 'prelaunch-misconfigured' : 'not-subscribable';
        result.unitPrice = result.oneTimeUnit;
        result.total = result.oneTimeUnit * quantity;
        result.fellBackToOneTime = true;
        result.ready = true;
        return result;
      }

      result.subscriptionEligible = true;
      result.cycle = cycle;
      result.plan = plan;
      result.sellingPlanId = plan.id;

      var tiers = tiersFor(variant);
      result.tiers = tiers;

      var tier = tiers.length ? getTier(tiers, quantity) : null;

      if (tier) {
        // State 3
        result.tier = tier;
        result.priceSource = 'tier';
        result.total = tier.total;
        result.unitPrice = tier.unit;
        result.savings = tier.discountAmount;
        result.freeShipping = tier.freeShipping;
        result.bounds = tierBounds(tiers);
        result.nextTier = nextBetterTier(tiers, quantity);
        result.ready = true;

        /*
         * Price of record, matching getSubscriptionPricing() on the product
         * page: when the resolved selling plan carries a real allocation
         * price, that is what Shopify charges for a subscription line, so it
         * is what the card shows. The bundle tier still gates which bag
         * counts are offered (bounds above) but no longer supplies the
         * number. Without an allocation price the tier total stands.
         */
        var tierPlanUnit = Number(plan.price) / 100;
        if (isFinite(tierPlanUnit) && tierPlanUnit > 0) {
          result.priceSource = 'plan';
          result.unitPrice = tierPlanUnit;
          result.total = tierPlanUnit * quantity;
          result.savings = Math.max(0, result.compareTotal - result.total);
        }

        return result;
      }

      /*
       * No tier matched this quantity. Bundle rules may still be in flight,
       * so report "pending" and let the caller hold off briefly rather than
       * printing one price and swapping it a beat later. Once the caller
       * passes settled:true we must return something usable.
       */
      if (!tiers.length && !input.settled) {
        result.reason = 'tiers-pending';
        return result;
      }

      /*
       * Settled, and no tier applies. The selling plan's own allocation
       * price is still real Appstle pricing, so use it when it exists and
       * actually beats the one-time price.
       *
       * If it does not exist, this product has a selling plan attached but
       * nothing behind it — no bundle entry and no allocation price. There
       * is no subscription price to show, so we do NOT offer a subscription
       * the store cannot honour. The product degrades to an ordinary
       * one-time Shopify product: no Subscribe option, no savings, no
       * schedule. Not every product is in the Subscribe & Save bundle.
       */
      var planUnit = Number(plan.price) / 100;
      // Same rule as getSubscriptionPricing() on the product page: any real
      // allocation price makes the plan purchasable.
      var hasPlanPrice = isFinite(planUnit) && planUnit > 0;

      if (!hasPlanPrice) {
        result.subscriptionEligible = false;
        result.cycle = null;
        result.plan = null;
        result.sellingPlanId = null;
        result.tiers = [];
        result.priceSource = 'onetime';
        result.unitPrice = result.oneTimeUnit;
        result.total = result.oneTimeUnit * quantity;
        result.bounds = { min: 1, max: 12 };
        result.reason = 'no-subscription-pricing';
        result.fellBackToOneTime = true;
        result.ready = true;
        return result;
      }

      result.priceSource = 'plan';
      result.unitPrice = planUnit;
      result.total = planUnit * quantity;
      result.savings = Math.max(0, result.compareTotal - result.total);
      result.bounds = tiers.length ? tierBounds(tiers) : { min: 1, max: 12 };
      result.nextTier = tiers.length ? nextBetterTier(tiers, quantity) : null;
      result.reason = tiers.length ? 'no-tier-for-quantity' : 'no-bundle-rule';
      result.ready = true;

      return result;
    }

    return {
      resolve: resolve,
      invalidate: invalidate,
      variants: variants,
      data: data,
      config: settings
    };
  }

  /* ================================================================== */

  global.PathwayAppstle = {
    // formatting
    money: money,
    moneyFromCents: moneyFromCents,
    parseJsonValue: parseJsonValue,
    bagLabel: bagLabel,
    formatDate: formatDate,
    parseIsoDateString: parseIsoDateString,

    // variants
    findVariantForOptions: findVariantForOptions,
    isOptionValueAvailable: isOptionValueAvailable,

    // inventory-aware initial fulfillment
    normalizeNonNegativeInt: normalizeNonNegativeInt,
    getInventoryStatus: getInventoryStatus,
    formatIsoDate: formatIsoDate,

    // cycles and schedule
    daysBetween: daysBetween,
    nextBillingDate: nextBillingDate,
    findPlanForCycle: findPlanForCycle,
    getCycleCandidates: getCycleCandidates,
    getPrelaunchPlanCycle: getPrelaunchPlanCycle,
    isPrelaunchConfigValid: isPrelaunchConfigValid,
    getSelectedCycle: getSelectedCycle,
    getSellingPlan: getSellingPlan,
    getCycleShipDate: getCycleShipDate,
    formatBillingDate: formatBillingDate,
    formatCustomerShipDate: formatCustomerShipDate,
    formatShipLabel: formatShipLabel,
    formatCycleWindow: formatCycleWindow,
    shortCycleLabel: shortCycleLabel,

    // Appstle tiers
    getAppstleRule: getAppstleRule,
    normalizeAppstleTiers: normalizeAppstleTiers,
    getTier: getTier,
    tierBounds: tierBounds,
    nextBetterTier: nextBetterTier,

    // engine
    createPricingEngine: createPricingEngine
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = global.PathwayAppstle;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
